import React from 'react';
import { Loader2 } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'dark' | 'ghost' | 'danger';
type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  // DS props
  loading?: boolean;
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  // Legacy aliases (backward-compatible)
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-gradient-to-r from-[#0066FF] to-[#00AAFF] text-white shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 hover:-translate-y-0.5',
  secondary:
    'bg-white text-neutral-700 border-2 border-neutral-200 hover:border-[#0066FF] hover:text-[#0066FF]',
  outline:
    'bg-white text-neutral-700 border-2 border-neutral-200 hover:border-[#0066FF] hover:text-[#0066FF]',
  dark: 'bg-neutral-900 text-white hover:bg-neutral-800',
  ghost: 'text-[#0066FF] hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950/50',
  danger: 'bg-red-500 text-white hover:bg-red-600',
};

const sizeClasses: Record<ButtonSize, string> = {
  xs: 'px-3 py-1.5 text-xs rounded-lg gap-1.5',
  sm: 'px-4 py-2 text-sm rounded-lg gap-1.5',
  md: 'px-6 py-3 text-sm rounded-xl gap-2',
  lg: 'px-8 py-3.5 text-base rounded-xl gap-2',
  xl: 'px-10 py-4 text-lg rounded-2xl gap-2.5',
};

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  loading,
  icon,
  iconRight,
  isLoading,
  leftIcon,
  rightIcon,
  fullWidth = false,
  className,
  disabled,
  ...props
}) => {
  const resolvedLoading = loading ?? isLoading ?? false;
  const resolvedIcon = icon ?? leftIcon;
  const resolvedIconRight = iconRight ?? rightIcon;
  const isDisabled = disabled || resolvedLoading;

  const buttonClasses = twMerge(
    'inline-flex items-center justify-center font-semibold transition-all duration-200',
    sizeClasses[size],
    isDisabled
      ? 'bg-neutral-200 text-neutral-400 dark:bg-neutral-800 dark:text-neutral-500 cursor-not-allowed shadow-none hover:translate-y-0'
      : variantClasses[variant],
    fullWidth && 'w-full',
    className
  );

  return (
    <button
      className={buttonClasses}
      disabled={isDisabled}
      {...props}
    >
      {resolvedLoading ? <Loader2 size={16} className="animate-spin" /> : resolvedIcon}
      {children}
      {!resolvedLoading && resolvedIconRight}
    </button>
  );
};
